import { useAppSelector } from '@/store/hooks';
import { fetchAuthSession } from '@aws-amplify/auth';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useEffect, useMemo, useState } from 'react';
import useWebSocket from 'react-use-websocket';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Handler = (data: any) => void;

const listeners = new Map<string, Set<Handler>>();

const poolId = import.meta.env.VITE_POOL_ID;

export const useSharedWebSocket = (url: string) => {
  const [socketUrl, setSocketUrl] = useState<string | null>(null);

  const authUserData = useAppSelector((state) => state.user.authUserData);

  const isLoginError = authUserData.error;
  const isCustomer = !!authUserData.data.customer?.c_id;

  useEffect(() => {
    const init = async () => {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      const finalUrl =
        accessToken && poolId
          ? `${url}?token=Bearer ${accessToken}&pool_id=${poolId}`
          : '';

      setSocketUrl(finalUrl);
    };

    init();
  }, [url]);

  const {
    sendJsonMessage,
    lastJsonMessage,
    lastMessage,
    readyState,
    sendMessage,
    getWebSocket,
  } = useWebSocket(socketUrl, {
    share: true,
    shouldReconnect: () => true,
    reconnectAttempts: 10,
    reconnectInterval: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
  });

  const { authStatus } = useAuthenticator((context) => [context.authStatus]);

  const handleClose = () => {
    const ws = getWebSocket();

    if (
      ws &&
      (ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING)
    ) {
      ws.close(1000, 'Closed by user');
    }
  };

  useEffect(() => {
    if (!lastMessage?.data) return;

    try {
      const parsed = JSON.parse(lastMessage.data);

      if (parsed.ping === true && readyState === 1) {
        sendJsonMessage({ pong: true });
      }
    } catch (e) {
      console.error('Parse error', e);
    }
  }, [lastMessage, readyState]);

  const rawMessage = useMemo(() => {
    if (lastJsonMessage) {
      return lastJsonMessage;
    }

    if (lastMessage?.data) {
      try {
        return JSON.parse(lastMessage.data);
      } catch {
        return lastMessage.data;
      }
    }

    return null;
  }, [lastJsonMessage, lastMessage]);

  useEffect(() => {
    if (rawMessage) {
      listeners.get('RAW')?.forEach((cb) => cb(rawMessage));
    }
  }, [rawMessage]);

  const subscribe = (type: string, cb: Handler) => {
    if (!listeners.has(type)) {
      listeners.set(type, new Set());
    }

    listeners.get(type)!.add(cb);

    return () => {
      listeners.get(type)?.delete(cb);
    };
  };

  useEffect(() => {
    if (isLoginError || isCustomer || authStatus === 'unauthenticated') {
      handleClose();
    }
  }, [authUserData, authStatus]);

  return {
    send: sendJsonMessage,
    subscribe,
    rawMessage,
    readyState,
    sendMessage,
    handleClose,
  };
};
